import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { CategoryIcon } from '@/components/CategoryIcon';
import curated from '@/lib/category-icons.json';

interface IconPickerProps {
  /** Current kebab icon name (may be any Phosphor icon, not just curated). */
  value: string;
  onChange: (name: string) => void;
}

interface IconOption {
  name: string;
  label: string;
}

type CuratedMeta = Record<string, { label: string; keywords: string[] }>;
const CURATED: CuratedMeta = curated;
const CURATED_OPTIONS: IconOption[] = Object.entries(CURATED).map(([name, meta]) => ({
  name,
  label: meta.label,
}));

type SearchModule = typeof import('@/lib/icon-search');

/**
 * Icon choice for a custom category: the curated set renders instantly from
 * the main bundle; typing searches the ENTIRE Phosphor library (~1,500 icons)
 * via a lazily-imported catalog chunk, results capped so the grid never holds
 * more than ~100 nodes. Any picked name renders app-wide through
 * CategoryIcon's dynamic path.
 */
export function IconPicker({ value, onChange }: IconPickerProps) {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [searchApi, setSearchApi] = useState<SearchModule | null>(null);
  const searchApiPromise = useRef<Promise<SearchModule> | null>(null);

  const trimmed = deferredQuery.trim();
  const searching = trimmed.length > 0;

  useEffect(() => {
    if (!searching || searchApi) return;
    let alive = true;
    (searchApiPromise.current ??= import('@/lib/icon-search')).then((mod) => {
      if (alive) setSearchApi(mod);
    });
    return () => {
      alive = false;
    };
  }, [searching, searchApi]);

  const results = useMemo<IconOption[]>(() => {
    if (!searching) return CURATED_OPTIONS;
    if (!searchApi) return [];
    // Curated-only fallback filter is unnecessary: searchIcons already boosts
    // curated matches and covers their pt-BR keywords.
    return searchApi.searchIcons(trimmed);
  }, [searching, searchApi, trimmed]);

  const selectedIsVisible = results.some((r) => r.name === value);

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 rounded-lg border border-border px-3 py-2 focus-within:border-border-brand">
        <MagnifyingGlassIcon aria-hidden className="size-4 shrink-0 text-text-tertiary" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar ícone…"
          aria-label="Buscar ícone"
          className="w-full bg-transparent text-base outline-none sm:text-sm"
        />
      </div>

      {searching && !searchApi ? (
        <p className="py-3 text-center text-xs text-text-tertiary" role="status">
          Carregando ícones…
        </p>
      ) : results.length === 0 ? (
        <p className="py-3 text-center text-xs text-text-tertiary">
          Nenhum ícone para “{trimmed}”.
        </p>
      ) : (
        // Plain toggle buttons, not listbox/option: those roles promise a
        // single-tab-stop + arrow-key contract this grid doesn't implement.
        // Same pattern as ColorSwatchPicker (aria-label + aria-pressed).
        <div className="flex max-h-48 flex-wrap content-start gap-2 overflow-y-auto overscroll-contain">
          {/* Keep the current (possibly off-results) selection visible so the
              active state never silently disappears mid-search. */}
          {!selectedIsVisible && value && (
            <IconTile name={value} label={CURATED[value]?.label ?? value} selected onSelect={onChange} />
          )}
          {results.map((r) => (
            <IconTile
              key={r.name}
              name={r.name}
              label={r.label}
              selected={r.name === value}
              onSelect={onChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function IconTile({
  name,
  label,
  selected,
  onSelect,
}: IconOption & { selected: boolean; onSelect: (name: string) => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Ícone ${label}`}
      title={label}
      onClick={() => onSelect(name)}
      className={`grid size-9 shrink-0 place-items-center rounded-lg border transition-colors ${
        selected
          ? 'border-border-brand bg-brand-100 text-brand-800'
          : 'border-border-subtle text-text-secondary'
      }`}
    >
      <CategoryIcon name={name} className="size-4.5" />
    </button>
  );
}

/** Exported for tests: curated options shown for an empty query. */
export { CURATED_OPTIONS };
